import { useState, type FormEvent } from 'react';
import { Loader2 } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { AuthApiError, emailAuth, sessionFromTokens } from '@/api/auth/emailAuth';
import { useAuth } from '@/auth/authContext';
import { Button } from '@/components/ui/button';
import { useT } from '@/i18n/locale';

import { AuthLayout } from './AuthLayout';
import {
  AuthField,
  AuthSwitch,
  FormAlert,
  OrDivider,
  PasswordField,
  SubmitButton,
} from './fields';
import { focusField, friendlyAuthError, LABEL_ACTION_HIT } from './form';
import { sanitizeNext } from './next';
import { SocialButtons } from './SocialButtons';

export function LoginScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = sanitizeNext(searchParams.get('next'));
  const { signIn, signOut, status, isAuthenticated, user } = useAuth();
  const t = useT();

  const [email, setEmail] = useState(searchParams.get('email') ?? '');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [challenge, setChallenge] = useState(false);
  const [busy, setBusy] = useState(false);

  // Restoring a session — show the door with a spinner, not a stale form.
  if (status === 'loading' || (isAuthenticated && !user)) {
    return (
      <AuthLayout title={t('login.heading')}>
        <div className="flex justify-center py-10" role="status">
          <Loader2 className="h-6 w-6 animate-spin text-ink-secondary" aria-hidden="true" />
          <span className="sr-only">{t('common.loading')}</span>
        </div>
      </AuthLayout>
    );
  }

  // Already signed in — no form. Show who the visitor is and let them continue
  // straight in (honouring `?next=`) or sign out to switch accounts.
  if (isAuthenticated) {
    const displayName = user?.name;
    const userEmail = user?.email;
    return (
      <AuthLayout title={t('login.signedInAs')}>
        <div className="mt-4 flex flex-col gap-1">
          {displayName ? <p className="text-headline text-ink">{displayName}</p> : null}
          {userEmail ? <p className="text-subhead text-ink-secondary">{userEmail}</p> : null}
        </div>
        <div className="mt-6 flex flex-col gap-3">
          <Button
            type="button"
            variant="accent"
            size="lg"
            className="w-full"
            onClick={() => navigate(next, { replace: true })}
          >
            {t('login.continueToApp')}
          </Button>
          <Button
            type="button"
            variant="neutral"
            size="lg"
            className="w-full"
            onClick={() => void signOut()}
          >
            {t('login.signOut')}
          </Button>
        </div>
      </AuthLayout>
    );
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const nextEmailError = email.trim() ? null : t('login.error.enterEmail');
    const nextPasswordError = password ? null : t('login.error.enterPassword');
    setEmailError(nextEmailError);
    setPasswordError(nextPasswordError);
    setFormError(null);
    setChallenge(false);
    if (nextEmailError || nextPasswordError) {
      focusField(nextEmailError ? 'email' : 'password');
      return;
    }

    setBusy(true);
    try {
      const response = await emailAuth.signIn(email.trim(), password);
      if ('challenge' in response) {
        // The backend has no endpoint to answer this challenge, so surface a
        // clear path instead of a dead-end form.
        setChallenge(true);
        return;
      }
      await signIn(sessionFromTokens(response, 'password', email.trim()));
      navigate(next, { replace: true });
    } catch (error) {
      if (error instanceof AuthApiError && error.status === 403) {
        navigate(`/confirm?email=${encodeURIComponent(email.trim())}`);
        return;
      }
      if (error instanceof AuthApiError && error.status === 401) {
        setFormError(t('login.error.invalid'));
        return;
      }
      if (error instanceof AuthApiError && error.status === 0) {
        setFormError(t('login.error.network'));
        return;
      }
      setFormError(friendlyAuthError(error, t, 'login.error.unknown'));
    } finally {
      setBusy(false);
    }
  }

  const forgotTo = `/forgot${email.trim() ? `?email=${encodeURIComponent(email.trim())}` : ''}`;
  const signupTo = next === '/' ? '/signup' : `/signup?next=${encodeURIComponent(next)}`;

  return (
    <AuthLayout title={t('login.heading')} lede={t('login.lede')}>
      <div className="flex flex-col gap-6">
        <SocialButtons returnTo={next} />
        <OrDivider>{t('auth.orEmail')}</OrDivider>
        <form onSubmit={onSubmit} noValidate className="flex flex-col gap-5">
          {formError ? <FormAlert>{formError}</FormAlert> : null}
          {challenge ? (
            <p role="alert" className="text-caption text-ink-secondary">
              {t('login.challengeBefore')}{' '}
              <Link to={forgotTo} className="font-medium text-ink underline">
                {t('login.challengeLink')}
              </Link>{' '}
              {t('login.challengeAfter')}
            </p>
          ) : null}
          <AuthField
            id="email"
            label={t('login.email')}
            type="email"
            autoComplete="email"
            autoCapitalize="none"
            value={email}
            error={emailError}
            disabled={busy}
            onChange={(event) => {
              setEmail(event.target.value);
              setEmailError(null);
            }}
          />
          <PasswordField
            id="password"
            label={t('login.password')}
            autoComplete="current-password"
            value={password}
            error={passwordError}
            disabled={busy}
            onChange={(event) => {
              setPassword(event.target.value);
              setPasswordError(null);
            }}
            labelTrailing={
              <Link
                to={forgotTo}
                className={`${LABEL_ACTION_HIT} text-subhead text-ink-secondary underline-offset-4 hover:text-ink hover:underline`}
              >
                {t('login.forgotPasswordLink')}
              </Link>
            }
          />
          <SubmitButton busy={busy}>{t('login.signIn')}</SubmitButton>
        </form>
      </div>

      <div className="mt-8">
        <AuthSwitch
          prompt={t('login.newToScreenAtlas')}
          to={signupTo}
          action={t('login.createAccount')}
        />
      </div>
    </AuthLayout>
  );
}
